import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { useBatches } from "hooks/api/use-batches";

import { useCurrentClient } from "hooks/api/use-clients";
import { useGetBatchStatusesQuery } from "hooks/api/dictionary/dictionary";

import type { BatchItem } from "containers/admin/clients/client/approval-queue/approval-queue.types";
import {
  ADD_TO_BATCH_LIST_PARAMS,
  filterBatchesByStatus,
} from "containers/admin/clients/client/approval-queue/approval-queue.helpers";

export type UseAddToBatchSelectOptions = {
  isLatestBatchLoading?: boolean;
  filterBatchesByActiveStatus?: boolean;
  latestBatchId?: string;
  /** Touchpoint version's existing batch — pre-selects current batch when changing; skips latest-batch suggestion. */
  versionBatchId?: string;
  setIsSubmitDisabled?: (isDisabled: boolean) => void;
  /** Syncs parent state when selection changes (creative modal tracks batch outside RHF). */
  onBatchIdChange?: (batchId: string, batchName?: string) => void;
};

/**
 * Batch select field logic for add-to-batch modals.
 * Must run under a react-hook-form `FormProvider` (e.g. `FormDialog` children with `batchId` in defaultValues).
 */
export const useAddToBatchSelect = ({
  isLatestBatchLoading = false,
  filterBatchesByActiveStatus = true,
  latestBatchId,
  versionBatchId,
  setIsSubmitDisabled,
  onBatchIdChange,
}: UseAddToBatchSelectOptions = {}) => {
  const { setValue, watch } = useFormContext();
  const selectedBatchId = watch("batchId");

  const onBatchIdChangeRef = useRef(onBatchIdChange);
  onBatchIdChangeRef.current = onBatchIdChange;

  const setIsSubmitDisabledRef = useRef(setIsSubmitDisabled);
  setIsSubmitDisabledRef.current = setIsSubmitDisabled;

  const currentClient = useCurrentClient();
  const { data: batchPage, isLoading: isBatchesLoading } = useBatches({
    clientId: currentClient.id,
    ...ADD_TO_BATCH_LIST_PARAMS,
  });
  const { data: batchStatuses = [], isLoading: isBatchStatusesLoading } =
    useGetBatchStatusesQuery();

  const batches: BatchItem[] = useMemo(() => {
    const items = batchPage?.items ?? [];
    return filterBatchesByActiveStatus ? filterBatchesByStatus(items) : items;
  }, [batchPage?.items, filterBatchesByActiveStatus]);

  const isBatchSelectReady =
    !isBatchesLoading && !isBatchStatusesLoading && batchStatuses.length > 0;

  const isSelectLoading = !isBatchSelectReady || isLatestBatchLoading;

  const availableBatchIds = useMemo(() => batches.map((batch) => batch.id), [batches]);

  useEffect(() => {
    if (!versionBatchId || !isBatchSelectReady) {
      return;
    }
    if (!availableBatchIds.includes(versionBatchId)) {
      return;
    }
    if (selectedBatchId) {
      return;
    }

    setValue("batchId", versionBatchId, { shouldValidate: true });
    const currentBatch = batches.find((batch) => batch.id === versionBatchId);
    onBatchIdChangeRef.current?.(versionBatchId, currentBatch?.name);
  }, [versionBatchId, isBatchSelectReady, availableBatchIds, selectedBatchId, setValue, batches]);

  useEffect(() => {
    if (versionBatchId) {
      return;
    }
    if (!latestBatchId || !isBatchSelectReady) {
      return;
    }
    if (!availableBatchIds.includes(latestBatchId)) {
      return;
    }
    if (selectedBatchId) {
      return;
    }

    setValue("batchId", latestBatchId, { shouldValidate: true });
    const latestBatch = batches.find((batch) => batch.id === latestBatchId);
    onBatchIdChangeRef.current?.(latestBatchId, latestBatch?.name);
  }, [
    versionBatchId,
    latestBatchId,
    isBatchSelectReady,
    availableBatchIds,
    selectedBatchId,
    setValue,
    batches,
  ]);

  const isSameAsVersionBatch =
    !!versionBatchId && !!selectedBatchId && selectedBatchId === versionBatchId;

  useEffect(() => {
    setIsSubmitDisabledRef.current?.(!selectedBatchId || isSelectLoading || isSameAsVersionBatch);
  }, [selectedBatchId, isSelectLoading, isSameAsVersionBatch]);

  return {
    batches,
    batchStatuses,
    isSelectLoading,
  };
};
